import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { getMembers, createMember, updateMember, deleteMember, refreshMembers, onCacheUpdate } from '../services/api';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MembersSection = ({ onSelectMember, isAdmin = false }) => {
  const [members, setMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [flippedCards, setFlippedCards] = useState(new Set());
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [notif, setNotif] = useState(null);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [memberBalances, setMemberBalances] = useState({}); // Store balances for each member
  const [memberTxns, setMemberTxns] = useState({}); // Store last 3 wcash transactions per member
  const [hideBalances, setHideBalances] = useState({}); // Track which member balances are hidden

  const showNotif = (msg, type = 'info') => {
    setNotif({ msg, type });
    setTimeout(() => setNotif(null), 3000);
  };

  const [formData, setFormData] = useState({
    name: '',
    bday: '',
    start: '',
    valid: '',
    cat: 'gold',
    phone: '',
    email: '',
    instagram: '',
    initial_wcash: '',  // Initial Wcash balance
    // Novos campos para contrato
    profession: '',
    cpf: '',
    rg: '',
    nationality: 'Brasileiro(a)',
    marital_status: '',
    address: '',
    city: '',
    state: '',
    cep: '',
  });
  
  const [professions, setProfessions] = useState([]);
  const [maritalStatusList, setMaritalStatusList] = useState([]);
  const [showDocScanner, setShowDocScanner] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractHtml, setContractHtml] = useState('');
  const [contractMember, setContractMember] = useState(null);
  const [documentImage, setDocumentImage] = useState(null); // Preview da imagem do documento
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signingContract, setSigningContract] = useState(null);
  const [facialImage, setFacialImage] = useState(null);
  const [signatureLoading, setSignatureLoading] = useState(false);

  useEffect(() => {
    // Force refresh on mount to ensure fresh data
    handleRefresh();
    
    // Load professions and marital status lists
    loadDropdownOptions();
    
    // Listen for member updates
    const unsubscribe = onCacheUpdate('members', loadMembers);
    return () => unsubscribe();
  }, []);
  
  const loadDropdownOptions = async () => {
    try {
      const [profRes, maritalRes] = await Promise.all([
        axios.get(`${API}/contracts/professions`),
        axios.get(`${API}/contracts/marital-status`)
      ]);
      setProfessions(profRes.data.professions || []);
      setMaritalStatusList(maritalRes.data.marital_status || []);
    } catch (error) {
      console.error('Erro ao carregar opções:', error);
    }
  };

  const loadMembers = async () => {
    try {
      setLoading(true);
      // Hard timeout: never leave the spinner hanging forever (PWA UX)
      const timeoutId = setTimeout(() => setLoading(false), 15000);
      const response = await getMembers();
      clearTimeout(timeoutId);
      setMembers(response.data);
      // Selecionar primeiro membro automaticamente
      if (response.data.length > 0 && onSelectMember) {
        onSelectMember(response.data[0]);
      }
    } catch (error) {
      console.error('Erro ao carregar membros:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const response = await refreshMembers();
      setMembers(response.data);
      if (response.data.length > 0 && onSelectMember) {
        onSelectMember(response.data[0]);
      }
    } catch (error) {
      console.error('Erro ao atualizar membros:', error);
      // Fallback to cached
      loadMembers();
    }
    setIsRefreshing(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMember) {
        await updateMember(editingMember.id, formData);
      } else {
        await createMember(formData);
      }
      await loadMembers();
      handleCloseModal();
    } catch (error) {
      console.error('Erro ao salvar membro:', error);
      showNotif('Erro ao salvar membro', 'warn');
    }
  };

  const handleEdit = async (member) => {
    setEditingMember(member);
    
    // Load current Wcash balance
    let currentWcash = 0;
    try {
      const res = await axios.get(`${API}/wallet/${member.id}/balance`);
      currentWcash = res.data?.wcash_balance || 0;
    } catch (e) {
      console.error('Error loading wcash balance:', e);
    }
    
    setFormData({
      name: member.name,
      social_name: member.social_name || '',
      bday: member.bday,
      start: member.start,
      valid: member.valid,
      cat: member.cat,
      phone: member.phone || '',
      email: member.email || '',
      instagram: member.instagram || '',
      initial_wcash: currentWcash > 0 ? currentWcash.toString() : '',
      // Novos campos
      profession: member.profession || '',
      cpf: member.cpf || '',
      rg: member.rg || '',
      nationality: member.nationality || 'Brasileiro(a)',
      marital_status: member.marital_status || '',
      address: member.address || '',
      city: member.city || '',
      state: member.state || '',
      cep: member.cep || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este membro?')) {
      try {
        await deleteMember(id);
        await loadMembers();
      } catch (error) {
        console.error('Erro ao deletar membro:', error);
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingMember(null);
    setDocumentImage(null);
    setFormData({
      name: '',
      social_name: '',  // Nome Social para cartões
      bday: '',
      start: '',
      valid: '',
      cat: 'gold',
      phone: '',
      email: '',
      instagram: '',
      initial_wcash: '',
      profession: '',
      cpf: '',
      rg: '',
      nationality: 'Brasileiro(a)',
      marital_status: '',
      address: '',
      city: '',
      state: '',
      cep: '',
    });
  };
  
  // Scanner de documento com IA
  const handleDocumentScan = async (imageBase64) => {
    setScanLoading(true);
    try {
      const res = await axios.post(`${API}/contracts/scan-document`, {
        image_base64: imageBase64
      });
      
      if (res.data.success && res.data.data) {
        const data = res.data.data;
        setFormData(prev => ({
          ...prev,
          name: data.name || prev.name,
          cpf: data.cpf || prev.cpf,
          rg: data.rg || prev.rg,
          nationality: data.nationality || prev.nationality,
          address: data.address || prev.address,
          city: data.city || prev.city,
          state: data.state || prev.state,
          bday: data.birth_date ? data.birth_date.split('/').slice(0, 2).join('/') : prev.bday,
        }));
        showNotif('Dados extraídos com sucesso!', 'success');
      } else {
        showNotif('Não foi possível extrair os dados', 'warn');
      }
    } catch (error) {
      console.error('Erro no scanner:', error);
      showNotif('Erro ao processar documento', 'warn');
    } finally {
      setScanLoading(false);
      setShowDocScanner(false);
    }
  };
  
  // Gerar contrato
  const handleGenerateContract = async (member) => {
    try {
      const res = await axios.post(`${API}/contracts/generate`, {
        member_id: member.id
      });
      
      if (res.data.success) {
        setContractHtml(res.data.html);
        setContractMember(member);
        setSigningContract(res.data);
        setShowContractModal(true);
        showNotif('Contrato gerado!', 'success');
      }
    } catch (error) {
      console.error('Erro ao gerar contrato:', error);
      showNotif('Erro ao gerar contrato', 'warn');
    }
  };
  
  // Assinar contrato digitalmente
  const handleSignContract = async () => {
    if (!facialImage || !signingContract) {
      showNotif('Tire uma foto facial para assinar', 'warn');
      return;
    }
    
    setSignatureLoading(true);
    try {
      // Obter geolocalização se disponível
      let geolocation = {};
      if (navigator.geolocation) {
        try {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          });
          geolocation = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy
          };
        } catch (e) {
          console.log('Geolocalização não disponível');
        }
      }
      
      const res = await axios.post(`${API}/contracts/${signingContract.contract_id}/sign`, {
        facial_image: facialImage.includes(',') ? facialImage.split(',')[1] : facialImage,
        device_info: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language
        },
        geolocation
      });
      
      if (res.data.success) {
        showNotif(`Contrato assinado! Código: ${res.data.validation_code}`, 'success');
        setShowSignatureModal(false);
        setFacialImage(null);
        loadMembers(); // Recarregar membros
      }
    } catch (error) {
      console.error('Erro ao assinar:', error);
      showNotif('Erro ao assinar contrato', 'warn');
    } finally {
      setSignatureLoading(false);
    }
  };

  const toggleFlip = (id) => {
    const newFlipped = new Set(flippedCards);
    if (newFlipped.has(id)) {
      newFlipped.delete(id);
    } else {
      newFlipped.add(id);
      // Load balance when card is flipped
      loadMemberBalance(id);
    }
    setFlippedCards(newFlipped);
  };

  // Load Wcash balance for a specific member
  const loadMemberBalance = async (memberId) => {
    if (memberBalances[memberId] !== undefined) return; // Already loaded
    try {
      const res = await axios.get(`${API}/wallet/${memberId}/wcash`);
      setMemberBalances(prev => ({
        ...prev,
        [memberId]: res.data?.wcash_balance || 0
      }));
    } catch (e) {
      // No wallet or error - set to null
      setMemberBalances(prev => ({
        ...prev,
        [memberId]: null
      }));
    }
    // Also lazy-load the last 3 wcash transactions (independent of balance success)
    if (memberTxns[memberId] === undefined) {
      try {
        const r = await axios.get(`${API}/wallet/${memberId}/cashback`);
        const list = Array.isArray(r.data) ? r.data.slice(0, 3) : [];
        setMemberTxns(prev => ({ ...prev, [memberId]: list }));
      } catch (e) {
        setMemberTxns(prev => ({ ...prev, [memberId]: [] }));
      }
    }
  };

  const [memberCardIndex, setMemberCardIndex] = useState(0);

  const filteredMembers = members.filter(
    (m) =>
      m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.id.includes(searchTerm)
  );

  const getCategoryColor = (cat) => {
    switch (cat) {
      case 'diamond':
        return '#b9f2ff';
      case 'black':
        return '#a0a0a0';
      default:
        return '#c0c0c0';
    }
  };

  return (
    <div className="space-y-6">
      {/* Notification */}
      {notif && (
        <div className={`px-4 py-2.5 rounded-xl text-xs font-['Jost'] ${
          notif.type === 'success' ? 'bg-green-500/10 border border-green-500/15 text-green-400/80' :
          notif.type === 'warn' ? 'bg-amber-500/8 border border-amber-500/15 text-amber-400/80' :
          'bg-white/5 border border-white/8 text-white/50'
        }`} data-testid="members-notif">{notif.msg}</div>
      )}
      {/* Toolbar */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex gap-2 flex-1">
          <input
            type="text"
            placeholder="Pesquisar sócio ou ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 px-4 py-3 bg-[#141418] border border-white/10 rounded-lg text-white focus:border-[#c0c0c0] outline-none"
            data-testid="search-member-input"
          />
          {/* Refresh Button */}
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="px-3 py-3 text-white/70 hover:text-white transition disabled:opacity-50"
            data-testid="refresh-members-btn"
            title="Atualizar lista de sócios"
          >
            <svg 
              className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor" 
              strokeWidth={1.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>
          </motion.button>
        </div>
        {/* Shiny Button - Only visible for Admin */}
        {isAdmin && (
          <button
            onClick={() => setShowModal(true)}
            className="group relative px-8 py-3.5 rounded-full overflow-hidden font-['Jost'] font-medium text-white/90 tracking-wide transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
            data-testid="add-member-button"
            style={{
              background: 'linear-gradient(135deg, rgba(37,99,235,0.9) 0%, rgba(59,130,246,0.8) 50%, rgba(37,99,235,0.9) 100%)',
              boxShadow: '0 0 30px rgba(59,130,246,0.4), 0 0 60px rgba(59,130,246,0.2), inset 0 1px 0 rgba(255,255,255,0.2)',
            }}
          >
          {/* Animated shine effect */}
          <div 
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
              animation: 'shineMove 2s ease-in-out infinite',
            }}
          />
          {/* Dotted pattern overlay */}
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)',
              backgroundSize: '8px 8px',
            }}
          />
          {/* Glow border */}
          <div 
            className="absolute inset-0 rounded-full"
            style={{
              border: '1px solid rgba(147,197,253,0.5)',
              boxShadow: 'inset 0 0 20px rgba(59,130,246,0.3)',
            }}
          />
          <span className="relative z-10 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Novo Membro
          </span>
        </button>
        )}
      </div>

      {/* Cards Grid */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* CSS for animated border */}
          <style>{`
            @keyframes memberBorderLight {
              0% { stroke-dashoffset: 0; }
              100% { stroke-dashoffset: -1600; }
            }
            .member-border-light {
              animation: memberBorderLight 8s linear infinite;
            }
            @keyframes selectedGlow {
              0%, 100% { opacity: 0.3; }
              50% { opacity: 0.6; }
            }
            .selected-glow {
              animation: selectedGlow 2s ease-in-out infinite;
            }
            .card-face {
              -webkit-backface-visibility: hidden;
              backface-visibility: hidden;
            }
            .card-inner {
              transform-style: preserve-3d;
              -webkit-transform-style: preserve-3d;
            }
            @keyframes shineMove {
              0% { transform: translateX(-100%); }
              100% { transform: translateX(100%); }
            }
          `}</style>
          
          {/* Navigation dots + arrows - compact, above cards */}
            {filteredMembers.length > 1 && (
              <div className="flex justify-center items-center gap-3 mb-3">
                <button 
                  onClick={(e) => { e.stopPropagation(); setMemberCardIndex(prev => Math.max(0, prev - 1)); }}
                  disabled={memberCardIndex === 0}
                  className="w-7 h-7 flex items-center justify-center transition active:scale-90 disabled:opacity-20 text-white/70 hover:text-white">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                </button>
                <div className="flex items-center gap-1">
                  {filteredMembers.map((_, i) => (
                    <button key={i} onClick={(e) => { e.stopPropagation(); setMemberCardIndex(i); }}
                      className="rounded-full transition-all"
                      style={{ width: i === memberCardIndex ? '12px' : '4px', height: '4px', background: i === memberCardIndex ? 'rgba(59,130,246,0.7)' : 'rgba(255,255,255,0.15)' }} />
                  ))}
                </div>
                <button 
                  onClick={(e) => { e.stopPropagation(); setMemberCardIndex(prev => Math.min(filteredMembers.length - 1, prev + 1)); }}
                  disabled={memberCardIndex >= filteredMembers.length - 1}
                  className="w-7 h-7 flex items-center justify-center transition active:scale-90 disabled:opacity-20 text-white/70 hover:text-white">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
            )}

          {/* Card Stack */}
          <div className="relative w-full" style={{ height: '260px' }}>
            <AnimatePresence mode="popLayout">
            {filteredMembers.map((member, index) => {
              const offset = index - memberCardIndex;
              const isTop = offset === 0;
              if (offset < -1 || offset >= 5) return null;
              if (offset < 0) return null;
              
              const isSelected = selectedMemberId === member.id;
              const categoryColor = getCategoryColor(member.cat);
              const isFlipped = flippedCards.has(member.id);
            
            return (
            <motion.div
              key={member.id}
              layout
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ 
                scale: isTop ? 1 : 1 - offset * 0.04,
                y: isTop ? 0 : -(offset * 16),
                opacity: isTop ? 1 : Math.max(0.15, 1 - offset * 0.25),
                zIndex: filteredMembers.length - offset,
              }}
              exit={{ x: -350, opacity: 0, rotateZ: -8, transition: { duration: 0.35, ease: 'easeIn' } }}
              transition={{ type: 'spring', stiffness: 280, damping: 26 }}
              className="absolute inset-x-0 bottom-0 h-[220px] cursor-pointer"
              style={{ perspective: '1000px', pointerEvents: isTop ? 'auto' : 'none' }}
              onClick={() => {
                if (!isTop) return;
                if (isFlipped) return;
                
                if (isSelected) {
                  toggleFlip(member.id);
                } else {
                  setSelectedMemberId(member.id);
                  onSelectMember && onSelectMember(member);
                  showNotif(`${member.name} selecionado`, 'success');
                }
              }}
              data-testid={`member-card-${member.id}`}
            >
              <motion.div
                className="relative w-full h-full"
                style={{ transformStyle: 'preserve-3d' }}
              >
                {/* Front - hide when flipped using opacity */}
                <motion.div
                  className={`absolute w-full h-full rounded-2xl p-6 overflow-hidden transition-shadow duration-500 ${
                    isSelected ? 'shadow-lg' : ''
                  }`}
                  animate={{ 
                    rotateY: isFlipped ? 180 : 0,
                    opacity: isFlipped ? 0 : 1,
                  }}
                  transition={{ duration: 0.5 }}
                  style={{
                    background: isSelected 
                      ? `linear-gradient(145deg, rgba(30,30,35,0.98) 0%, rgba(15,15,20,0.98) 100%)`
                      : 'linear-gradient(145deg, rgba(26,26,31,1) 0%, rgba(10,10,12,1) 100%)',
                    boxShadow: isSelected ? `0 0 30px ${categoryColor}40, inset 0 1px 1px rgba(255,255,255,0.1)` : 'none',
                    visibility: isFlipped ? 'hidden' : 'visible',
                  }}
                >
                  {/* Animated border light effect - Always on */}
                  <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 400 220" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id={`cardGrad-${member.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="rgba(255,255,255,0)" />
                        <stop offset="40%" stopColor={`${categoryColor}99`} />
                        <stop offset="50%" stopColor="rgba(255,255,255,0.9)" />
                        <stop offset="60%" stopColor={`${categoryColor}99`} />
                        <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                      </linearGradient>
                    </defs>
                    <rect
                      x="1" y="1" width="398" height="218" rx="16" ry="16"
                      fill="none"
                      stroke={`url(#cardGrad-${member.id})`}
                      strokeWidth={isSelected ? "2" : "1.5"}
                      strokeDasharray="1400"
                      className="member-border-light"
                      style={{ opacity: isSelected ? 1 : 0.6 }}
                    />
                  </svg>
                  
                  {/* Selected glow overlay */}
                  {isSelected && (
                    <div 
                      className="absolute inset-0 rounded-2xl selected-glow pointer-events-none"
                      style={{ 
                        background: `radial-gradient(ellipse at center, ${categoryColor}15 0%, transparent 70%)`,
                      }}
                    />
                  )}

                  <div className="relative z-10 flex justify-between items-start mb-8">
                    <div className="flex items-center gap-3">
                      <img 
                        src="/w-logo-chrome.png" 
                        alt="W" 
                        className="w-14 h-14 object-contain transition-all duration-300" 
                        style={{ filter: isSelected ? `drop-shadow(0 0 10px ${categoryColor}80)` : `drop-shadow(0 0 5px ${categoryColor}40)` }}
                        onError={(e) => {
                          // Fallback se a imagem não carregar
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <span 
                        className="w-14 h-14 flex items-center justify-center text-3xl font-bold"
                        style={{ 
                          display: 'none',
                          color: categoryColor,
                          textShadow: isSelected ? `0 0 15px ${categoryColor}` : 'none'
                        }}
                      >
                        W
                      </span>
                      <div 
                        className={`font-['Jost'] text-base tracking-[0.25em] transition-all duration-300`} 
                        style={{ color: categoryColor, textShadow: isSelected ? `0 0 10px ${categoryColor}` : 'none' }}
                      >
                        WCLUB
                      </div>
                    </div>
                    <div
                      className={`text-[10px] px-3 py-1 rounded-full border uppercase transition-all duration-300 ${
                        isSelected ? 'bg-white/5' : ''
                      }`}
                      style={{ 
                        borderColor: categoryColor, 
                        color: categoryColor,
                        boxShadow: isSelected ? `0 0 10px ${categoryColor}40` : 'none'
                      }}
                    >
                      {member.cat}
                    </div>
                  </div>
                  <div className="relative z-10 space-y-2">
                    <div 
                      className={`text-lg font-semibold font-['Jost'] uppercase transition-all duration-300 ${
                        isSelected ? 'text-white' : 'text-white/90'
                      }`}
                      style={{ textShadow: isSelected ? '0 0 20px rgba(255,255,255,0.3)' : 'none' }}
                    >
                      {member.social_name || member.name}
                    </div>
                    <div className={`font-['Space_Mono'] text-sm tracking-wider transition-all duration-300 ${
                      isSelected ? 'opacity-90' : 'opacity-70'
                    }`}>
                      {`4512 8800 9000 ${member.id.slice(-4).padStart(4, '0')}`}
                    </div>
                  </div>
                  <div className={`absolute bottom-6 left-6 right-6 flex justify-between items-end text-[10px] transition-all duration-300 ${
                    isSelected ? 'opacity-60' : 'opacity-40'
                  }`}>
                    <span>SINCE: {member.start}</span>
                    {isSelected && !isFlipped && (
                      <span className="text-[9px] px-2 py-1 bg-white/10 rounded text-white/70 animate-pulse">
                        Toque para ver QR
                      </span>
                    )}
                    <span>EXP: {member.valid}</span>
                  </div>
                </motion.div>

                {/* Back - show when flipped */}
                <motion.div
                  className="absolute w-full h-full rounded-2xl p-4 overflow-hidden bg-[#0a0a0f]"
                  animate={{ 
                    rotateY: isFlipped ? 0 : -180,
                    opacity: isFlipped ? 1 : 0,
                  }}
                  transition={{ duration: 0.5 }}
                  style={{
                    visibility: isFlipped ? 'visible' : 'hidden',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFlip(member.id);
                  }}
                >
                  {/* Solid background to hide front */}
                  <div className="absolute inset-0 bg-[#0a0a0f] rounded-2xl" />
                  
                  {/* Border */}
                  <div 
                    className="absolute inset-0 rounded-2xl border"
                    style={{ borderColor: categoryColor, borderWidth: '1px' }}
                  />
                  
                  <div className="relative z-10 flex h-full gap-3">
                    {/* Left side - QR Code + Balance */}
                    <div className="flex flex-col items-center justify-between flex-shrink-0 py-1">
                      {/* Balance Display with eye toggle */}
                      <div className="text-center mb-2">
                        <div className="flex items-center justify-center gap-1.5 mb-0.5">
                          <p className="text-[8px] text-white/40 uppercase tracking-wider">Saldo Wcash</p>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setHideBalances(prev => ({
                                ...prev,
                                [member.id]: !prev[member.id]
                              }));
                            }}
                            className="text-white/30 hover:text-white/50 transition"
                            data-testid={`toggle-balance-${member.id}`}
                          >
                            {hideBalances[member.id] ? (
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                              </svg>
                            ) : (
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            )}
                          </button>
                        </div>
                        <p className="text-white/90 text-lg font-light" style={{ fontFamily: "'Space Mono', monospace" }}>
                          {memberBalances[member.id] === undefined ? (
                            <span className="text-white/30 text-xs">...</span>
                          ) : memberBalances[member.id] === null ? (
                            <span className="text-white/30 text-xs">Sem carteira</span>
                          ) : hideBalances[member.id] ? (
                            <span className="text-white/60">••••••</span>
                          ) : (
                            `${memberBalances[member.id].toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          )}
                        </p>
                      </div>
                      {/* Últimas 3 transações Wcash — preview Apple-Card style.
                          Hidden when balance is hidden to respect privacy. */}
                      {!hideBalances[member.id] && Array.isArray(memberTxns[member.id]) && memberTxns[member.id].length > 0 && (
                        <div
                          className="w-full mb-2 space-y-0.5"
                          data-testid={`member-txns-${member.id}`}
                        >
                          {memberTxns[member.id].map((t) => {
                            const positive = (t.amount || 0) >= 0;
                            const dateStr = (() => {
                              try {
                                const d = new Date(t.created_at);
                                const dd = String(d.getDate()).padStart(2, '0');
                                const mm = String(d.getMonth() + 1).padStart(2, '0');
                                return `${dd}/${mm}`;
                              } catch { return ''; }
                            })();
                            const absAmount = Math.abs(t.amount || 0);
                            return (
                              <div
                                key={t.id}
                                className="flex items-center justify-between gap-1 px-1 text-[8px] font-['Space_Mono'] leading-tight"
                                style={{ opacity: 0.55 }}
                              >
                                <span className="text-white/45 tabular-nums">{dateStr}</span>
                                <span className={positive ? 'text-emerald-400/80' : 'text-rose-400/80'}>
                                  {positive ? '+' : '−'}{absAmount.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {/* QR Code - also hideable - LARGER SIZE */}
                      <div className={`bg-white p-3 rounded-xl transition-all ${hideBalances[member.id] ? 'opacity-20 blur-sm' : ''}`}>
                        <img
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=WCLUB:${member.id}&margin=0&format=png`}
                          alt="QR Code"
                          className="w-28 h-28"
                          data-testid={`member-qr-${member.id}`}
                        />
                      </div>
                      <p className="text-[9px] text-white/50 mt-2 text-center font-medium">Check-in</p>
                    </div>
                    {/* Buttons - right side */}
                    <div className="flex flex-col gap-1.5 flex-1 justify-center min-w-0">
                      {isAdmin && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(member);
                          }}
                          className="w-full px-2 py-1.5 bg-white/[0.06] border border-white/[0.08] rounded-lg text-[10px] text-white/60 hover:bg-white/[0.12] transition touch-manipulation"
                          data-testid={`edit-member-${member.id}`}
                        >
                          Editar
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFlip(member.id);
                        }}
                        className="w-full px-2 py-1.5 bg-white/90 text-black rounded-lg text-[10px] font-medium hover:bg-white transition touch-manipulation"
                      >
                        ← Voltar
                      </button>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          const memberUrl = `${window.location.origin}?member=${member.id}`;
                          
                          if (navigator.share) {
                            try {
                              await navigator.share({
                                title: `Cartão WClub - ${member.name}`,
                                text: `Olá ${member.name}, acesse seu cartão digital WClub:`,
                                url: memberUrl,
                              });
                            } catch (err) {
                              if (err.name !== 'AbortError') {
                                console.error('Erro ao compartilhar:', err);
                              }
                            }
                          } else {
                            navigator.clipboard.writeText(memberUrl);
                            showNotif('Link copiado!', 'success');
                          }
                        }}
                        className="w-full px-2 py-1.5 bg-blue-500/15 border border-blue-500/20 text-blue-400 rounded-lg text-[10px] hover:bg-blue-500/25 transition touch-manipulation"
                        data-testid={`share-member-${member.id}`}
                      >
                        Compartilhar
                      </button>
                      {isAdmin && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGenerateContract(member);
                          }}
                          className="w-full px-2 py-1.5 bg-emerald-500/15 border border-emerald-500/20 text-emerald-400 rounded-lg text-[10px] hover:bg-emerald-500/25 transition touch-manipulation flex items-center justify-center gap-1"
                          data-testid={`contract-member-${member.id}`}
                        >
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Gerar Contrato
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(member.id);
                          }}
                          className="w-full px-2 py-1.5 bg-slate-500/10 border border-slate-500/15 text-slate-400/80 rounded-lg text-[10px] hover:bg-slate-500/20 transition touch-manipulation"
                          data-testid={`delete-member-${member.id}`}
                        >
                          Excluir
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          )})}
          </AnimatePresence>
          </div>
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 overflow-y-auto"
            onClick={handleCloseModal}
          >
            <motion.div
              initial={{ scale: 0.9, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 50 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#141418] rounded-2xl p-6 max-w-md w-full border border-white/10 my-8 max-h-[90vh] overflow-y-auto"
            >
              <h2 className="text-2xl font-bold font-['Jost'] text-[#c0c0c0] mb-6 sticky top-0 bg-[#141418] py-2 -mt-2">
                {editingMember ? 'Editar Membro' : 'Novo Membro'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  placeholder="Nome Completo (para contratos)"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-[#0a0a0c] border border-white/10 rounded-lg"
                  data-testid="member-name-input"
                />
                <input
                  type="text"
                  placeholder="Nome Social (para cartões) - opcional"
                  value={formData.social_name || ''}
                  onChange={(e) => setFormData({ ...formData, social_name: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0a0a0c] border border-white/10 rounded-lg text-amber-400/80"
                  data-testid="member-social-name-input"
                />
                <input
                  type="text"
                  placeholder="Aniversário (DD/MM)"
                  value={formData.bday}
                  onChange={(e) => setFormData({ ...formData, bday: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-[#0a0a0c] border border-white/10 rounded-lg"
                />
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Início"
                    value={formData.start}
                    onChange={(e) => setFormData({ ...formData, start: e.target.value })}
                    required
                    className="px-4 py-3 bg-[#0a0a0c] border border-white/10 rounded-lg"
                  />
                  <input
                    type="text"
                    placeholder="Validade"
                    value={formData.valid}
                    onChange={(e) => setFormData({ ...formData, valid: e.target.value })}
                    required
                    className="px-4 py-3 bg-[#0a0a0c] border border-white/10 rounded-lg"
                  />
                </div>
                <select
                  value={formData.cat}
                  onChange={(e) => setFormData({ ...formData, cat: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0a0a0c] border border-white/10 rounded-lg"
                >
                  <option value="gold">Gold Member</option>
                  <option value="black">Black Member</option>
                  <option value="diamond">Diamond Member</option>
                  <option value="vip">VIP Member</option>
                  <option value="daypass">Day Pass</option>
                </select>
                <input
                  type="tel"
                  placeholder="Telefone"
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0a0a0c] border border-white/10 rounded-lg"
                  data-testid="member-phone-input"
                />
                <input
                  type="email"
                  placeholder="E-mail"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0a0a0c] border border-white/10 rounded-lg"
                  data-testid="member-email-input"
                />
                <input
                  type="text"
                  placeholder="Instagram (ex: @usuario)"
                  value={formData.instagram || ''}
                  onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0a0a0c] border border-white/10 rounded-lg"
                  data-testid="member-instagram-input"
                />
                
                {/* Wcash Balance */}
                <div className="relative">
                  <input
                    type="number"
                    placeholder={editingMember ? "Saldo Wcash atual (R$)" : "Saldo inicial Wcash (R$)"}
                    value={formData.initial_wcash || ''}
                    onChange={(e) => setFormData({ ...formData, initial_wcash: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0a0a0c] border border-violet-500/30 rounded-lg pr-16"
                    data-testid="member-wcash-input"
                    min="0"
                    step="0.01"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-violet-400/60 text-sm">W$</span>
                </div>
                
                {/* Divisor - Dados para Contrato */}
                <div className="border-t border-white/10 pt-4 mt-4">
                  <h3 className="text-sm font-['Jost'] text-white/60 uppercase tracking-wider mb-4">Dados para Contrato</h3>
                  
                  {/* Upload de Documento - Área expandida */}
                  <div className="mb-4 p-4 bg-[#0a0a0c] rounded-xl border border-white/10">
                    <p className="text-xs text-white/50 mb-3">Documento (RG/CNH) - A IA extrairá os dados automaticamente</p>
                    
                    {documentImage ? (
                      <div className="relative">
                        <img src={documentImage} alt="Documento" className="w-full h-40 object-cover rounded-lg" />
                        <button
                          type="button"
                          onClick={() => {
                            setDocumentImage(null);
                            setFormData(prev => ({ ...prev, document_photo: null }));
                          }}
                          className="absolute top-2 right-2 w-8 h-8 bg-black/60 rounded-full flex items-center justify-center text-white hover:bg-red-500/80 transition"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                        {scanLoading && (
                          <div className="absolute inset-0 bg-black/70 rounded-lg flex items-center justify-center">
                            <div className="text-center">
                              <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                              <p className="text-blue-400 text-xs">Extraindo dados...</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-2">
                        {/* Câmera */}
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onload = async () => {
                                  const base64Full = reader.result;
                                  const base64 = base64Full.split(',')[1];
                                  setDocumentImage(base64Full);
                                  setFormData(prev => ({ ...prev, document_photo: base64 }));
                                  // Auto scan
                                  handleDocumentScan(base64);
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                          <div className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-white/20 rounded-xl hover:border-blue-500/50 transition">
                            <svg className="w-8 h-8 text-blue-400 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="text-[10px] text-white/50">Câmera</span>
                          </div>
                        </label>
                        
                        {/* Galeria */}
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onload = async () => {
                                  const base64Full = reader.result;
                                  const base64 = base64Full.split(',')[1];
                                  setDocumentImage(base64Full);
                                  setFormData(prev => ({ ...prev, document_photo: base64 }));
                                  handleDocumentScan(base64);
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                          <div className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-white/20 rounded-xl hover:border-emerald-500/50 transition">
                            <svg className="w-8 h-8 text-emerald-400 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="text-[10px] text-white/50">Galeria</span>
                          </div>
                        </label>
                        
                        {/* Arquivos */}
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onload = async () => {
                                  const base64Full = reader.result;
                                  const base64 = base64Full.split(',')[1];
                                  setDocumentImage(base64Full);
                                  setFormData(prev => ({ ...prev, document_photo: base64 }));
                                  if (!file.name.endsWith('.pdf')) {
                                    handleDocumentScan(base64);
                                  }
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                          <div className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-white/20 rounded-xl hover:border-violet-500/50 transition">
                            <svg className="w-8 h-8 text-violet-400 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                            <span className="text-[10px] text-white/50">Arquivo</span>
                          </div>
                        </label>
                      </div>
                    )}
                  </div>
                  
                  {/* Profissão - Dropdown */}
                  <select
                    value={formData.profession || ''}
                    onChange={(e) => setFormData({ ...formData, profession: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0a0a0c] border border-white/10 rounded-lg mb-3"
                  >
                    <option value="">Selecione a Profissão</option>
                    {professions.map((prof) => (
                      <option key={prof} value={prof}>{prof}</option>
                    ))}
                  </select>
                  
                  {/* CPF e RG */}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <input
                      type="text"
                      placeholder="CPF (000.000.000-00)"
                      value={formData.cpf || ''}
                      onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
                      className="px-4 py-3 bg-[#0a0a0c] border border-white/10 rounded-lg"
                    />
                    <input
                      type="text"
                      placeholder="RG"
                      value={formData.rg || ''}
                      onChange={(e) => setFormData({ ...formData, rg: e.target.value })}
                      className="px-4 py-3 bg-[#0a0a0c] border border-white/10 rounded-lg"
                    />
                  </div>
                  
                  {/* Nacionalidade e Estado Civil */}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <input
                      type="text"
                      placeholder="Nacionalidade"
                      value={formData.nationality || ''}
                      onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                      className="px-4 py-3 bg-[#0a0a0c] border border-white/10 rounded-lg"
                    />
                    <select
                      value={formData.marital_status || ''}
                      onChange={(e) => setFormData({ ...formData, marital_status: e.target.value })}
                      className="px-4 py-3 bg-[#0a0a0c] border border-white/10 rounded-lg"
                    >
                      <option value="">Estado Civil</option>
                      {maritalStatusList.map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Endereço */}
                  <input
                    type="text"
                    placeholder="Endereço (Rua, Número, Complemento)"
                    value={formData.address || ''}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0a0a0c] border border-white/10 rounded-lg mb-3"
                  />
                  
                  {/* Cidade, Estado, CEP */}
                  <div className="grid grid-cols-3 gap-3">
                    <input
                      type="text"
                      placeholder="Cidade"
                      value={formData.city || ''}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="px-4 py-3 bg-[#0a0a0c] border border-white/10 rounded-lg"
                    />
                    <input
                      type="text"
                      placeholder="UF"
                      value={formData.state || ''}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="px-4 py-3 bg-[#0a0a0c] border border-white/10 rounded-lg"
                      maxLength={2}
                    />
                    <input
                      type="text"
                      placeholder="CEP"
                      value={formData.cep || ''}
                      onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
                      className="px-4 py-3 bg-[#0a0a0c] border border-white/10 rounded-lg"
                    />
                  </div>
                </div>
                
                <div className="flex gap-3 mt-6">
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-[#c0c0c0] to-[#e8e8e8] text-black font-semibold rounded-lg hover:shadow-lg transition"
                    data-testid="submit-member-button"
                  >
                    {editingMember ? 'Atualizar' : 'Criar'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-6 py-3 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Modal de Contrato */}
      <AnimatePresence>
        {showContractModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 flex items-center justify-center z-[60] p-4 overflow-y-auto"
            onClick={() => setShowContractModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 50 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col my-8"
            >
              {/* Header */}
              <div className="bg-[#141418] px-4 py-3">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <span className="text-emerald-400 font-bold">W</span>
                    </div>
                    <div>
                      <h3 className="text-white font-['Jost'] font-semibold">Contrato WClub</h3>
                      <p className="text-white/50 text-xs">{contractMember?.name} • {signingContract?.plan}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowContractModal(false)}
                    className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:bg-white/20 transition"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      const printWindow = window.open('', '_blank');
                      printWindow.document.write(contractHtml);
                      printWindow.document.close();
                      printWindow.print();
                    }}
                    className="px-3 py-2 bg-blue-500/20 text-blue-400 rounded-lg text-xs hover:bg-blue-500/30 transition flex items-center gap-1.5"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    Imprimir
                  </button>
                  
                  <button
                    onClick={async () => {
                      try {
                        const res = await axios.get(`${API}/contracts/${signingContract?.contract_id}/share`);
                        const shareUrl = `${window.location.origin}/sign/${res.data.share_token}`;
                        if (navigator.share) {
                          navigator.share({
                            title: `Contrato WClub - ${contractMember?.name}`,
                            text: 'Assine seu contrato WClub',
                            url: shareUrl
                          });
                        } else {
                          navigator.clipboard.writeText(shareUrl);
                          showNotif('Link de assinatura copiado!', 'success');
                        }
                      } catch (e) {
                        showNotif('Erro ao gerar link', 'warn');
                      }
                    }}
                    className="px-3 py-2 bg-emerald-500/20 text-emerald-400 rounded-lg text-xs hover:bg-emerald-500/30 transition flex items-center gap-1.5"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    Link Remoto
                  </button>
                  
                  <button
                    onClick={() => setShowSignatureModal(true)}
                    className="px-3 py-2 bg-violet-500/20 text-violet-400 rounded-lg text-xs hover:bg-violet-500/30 transition flex items-center gap-1.5"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    Assinar Agora
                  </button>
                </div>
              </div>
              
              {/* Contract Content */}
              <div className="flex-1 overflow-auto">
                <iframe
                  srcDoc={contractHtml}
                  className="w-full h-full min-h-[600px]"
                  title="Contrato"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Modal de Assinatura Digital com Face ID */}
      <AnimatePresence>
        {showSignatureModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 flex items-center justify-center z-[70] p-4"
            onClick={() => setShowSignatureModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 50 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#141418] rounded-2xl p-6 max-w-md w-full border border-violet-500/30"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center">
                  <svg className="w-6 h-6 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-['Jost'] font-semibold text-white">Assinatura Digital</h3>
                  <p className="text-xs text-white/50">Validação oficial com Face ID</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-4">
                  <p className="text-violet-300 text-sm mb-2">✓ Assinatura com reconhecimento facial</p>
                  <p className="text-violet-300 text-sm mb-2">✓ Timestamp oficial e IP registrados</p>
                  <p className="text-violet-300 text-sm mb-2">✓ Hash de validação único</p>
                  <p className="text-violet-300 text-sm">✓ Geolocalização (se disponível)</p>
                </div>
                
                <div className="border-2 border-dashed border-white/20 rounded-xl overflow-hidden">
                  {facialImage ? (
                    <div className="relative">
                      <img src={facialImage} alt="Foto facial" className="w-full h-48 object-cover" />
                      <button
                        type="button"
                        onClick={() => setFacialImage(null)}
                        className="absolute top-2 right-2 w-8 h-8 bg-black/60 rounded-full flex items-center justify-center text-white hover:bg-red-500/80 transition"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                      <div className="absolute bottom-2 left-2 px-2 py-1 bg-emerald-500/80 rounded text-white text-xs">
                        ✓ Foto capturada
                      </div>
                    </div>
                  ) : (
                    <label className="block cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        capture="user"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = () => {
                              setFacialImage(reader.result);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                      <div className="p-8 text-center hover:bg-white/5 transition">
                        <svg className="w-16 h-16 mx-auto text-violet-400/50 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-white/60 text-sm">Tire uma selfie para assinar</p>
                        <p className="text-white/30 text-xs mt-1">Face ID • Validação Oficial</p>
                      </div>
                    </label>
                  )}
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={handleSignContract}
                    disabled={!facialImage || signatureLoading}
                    className={`flex-1 py-3 rounded-lg font-semibold transition flex items-center justify-center gap-2 ${
                      facialImage && !signatureLoading
                        ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white hover:opacity-90'
                        : 'bg-white/10 text-white/30 cursor-not-allowed'
                    }`}
                  >
                    {signatureLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Assinando...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Confirmar Assinatura
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowSignatureModal(false);
                      setFacialImage(null);
                    }}
                    className="px-6 py-3 bg-white/5 border border-white/10 rounded-lg text-white/60 hover:bg-white/10 transition"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MembersSection;